import React, { useMemo } from "react";
import { useCoreContext } from "../../../../context/global/CoreContext";
import { Calendar } from "../../../../components/icons/svg/outline";
import { filterJobsByDateRange } from "../../../../utils/business/jobs";
import { getTodayInPT } from "../../../../utils/datetime";

/**
 * TicketJobSection Component
 * Reusable job selection and date picker for ticket forms
 * 
 * @param {Object} data - Form data object with `job` and `date_performed` fields
 * @param {Function} onChange - Callback when data changes
 * @param {boolean} readOnly - Whether the section is read-only
 * @param {boolean} jobReadOnly - Whether only the job field is read-only (overrides readOnly for job field)
 * @param {string} title - Section title (default: "Job Information")
 * @param {React.Component} icon - Icon component to display (default: Calendar)
 * @param {string} dateLabel - Label for date field (default: "Date Performed")
 * @param {string} dateFieldName - Field name for date (default: "date_performed")
 */
const TicketJobSection = ({
  data,
  onChange,
  readOnly = false,
  jobReadOnly = false,
  title = "Job Information",
  icon: Icon = Calendar,
  dateLabel = "Date Performed",
  dateFieldName = "date_performed",
}) => {
  const { allJobsList } = useCoreContext();

  // Filter out jobs that ended more than 1 year ago
  const activeJobsList = useMemo(() => {
    return filterJobsByDateRange(allJobsList);
  }, [allJobsList]);

  const handleJobChange = (e) => {
    const jobId = parseInt(e.target.value);
    onChange?.({ ...data, job: jobId });
  };

  const handleDateChange = (e) => {
    onChange?.({ ...data, [dateFieldName]: e.target.value });
  };

  const selectedJob = allJobsList?.find((job) => job.id === data?.job);

  // Determine if job field should be read-only
  const isJobReadOnly = readOnly || jobReadOnly;

  // For read-only mode, try to use job_number from API response first, then fallback to lookup
  const displayJobNumber =
    isJobReadOnly && data?.job_number ? data.job_number : selectedJob?.number;

  const displayJobDescription =
    isJobReadOnly && data?.job_description
      ? ` - ${data.job_description}`
      : selectedJob?.description
        ? ` - ${selectedJob.description}`
        : "";

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <Icon className="w-5 h-5 text-emerald-800" />
        {title} {readOnly && "(Read Only)"}
      </h2>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job ID *
          </label>
          {isJobReadOnly ? (
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {displayJobNumber || "N/A"}
              {displayJobDescription}
            </div>
          ) : (
            <select
              value={data?.job || ""}
              onChange={handleJobChange}
              className="w-full px-3 py-2 border h-[37px] border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Job ID</option>
              {activeJobsList?.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.number} - {job.description}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {dateLabel} *
          </label>
          {readOnly ? (
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {data?.[dateFieldName] || "N/A"}
            </div>
          ) : (
            <input
              type="date"
              value={data?.[dateFieldName] || getTodayInPT()}
              onChange={handleDateChange}
              className="w-full px-3 py-2 h-[37px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketJobSection;
